<template>
    <div
        :class="{
            'ds-polling-wrapper': true,
            'ds-polling-wrapper--pending': isPending,
            'ds-polling-wrapper--rejected': isRejected,
            'ds-polling-wrapper--resolved': isResolved,
        }"
    >
        <slot v-bind="{ status, isPending, isRejected, isResolved, requestCount, response: fetchedData }"></slot>
    </div>
</template>
<script>
export const DsPollingPromiseStatusEnum = Object.freeze({
    RESOLVED: 'RESOLVED',
    REJECTED: 'REJECTED',
    PENDING: 'PENDING',
});

/**
 * A component to poll DriveSparK's endpoints easily
 *
 * @version 4.0.0
 * @example ./examples/DsPollingWrapper.md
 */
export default {
    name: 'DsPollingWrapper',
    props: {
        /**
         * Default: { maxRequestsCount: 10, cacheRequest: false }
         */
        options: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Function to send the 'stop' signal to polling system.
         */
        shouldStopWhen: {
            type: Function,
            default: ({ data }) => data.data && data.data.completed === true,
        },
        /**
         * Url to poll
         */
        url: {
            type: String,
            required: true,
        },
        /**
         * Polling timeout to abort request.
         */
        timeout: {
            type: Number,
            default: 2500,
        },
        /**
         * Wheter to start polling on mount.
         */
        startOnMount: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            requestCount: 0,
            timeoutId: 0,
            status: null,
            fetchedData: null,
        };
    },
    computed: {
        theOptions() {
            return {
                maxRequestsCount: 10,
                cacheRequest: false,
                ...this.options,
            };
        },
        isPending() {
            return this.status === DsPollingPromiseStatusEnum.PENDING;
        },
        isResolved() {
            return this.status === DsPollingPromiseStatusEnum.RESOLVED;
        },
        isRejected() {
            return this.status === DsPollingPromiseStatusEnum.REJECTED;
        },
    },
    mounted() {
        if (this.startOnMount) {
            this.start();
        }
    },
    destroyed() {
        this.stop();
    },
    methods: {
        continuePollData(url, delay) {
            this.timeoutId = setTimeout(() => this.fetchData(url), delay);
        },
        async fetchData(url = null) {
            const requestUrl = url || this.url;

            if (!requestUrl || this.requestCount >= this.theOptions.maxRequestsCount) {
                return this.resolve();
            }

            this.status = DsPollingPromiseStatusEnum.PENDING;
            this.requestCount += 1;
            const delay = 500 * this.requestCount;
            const { httpRequest } = await import('@v4/http/request');
            return httpRequest({
                url: requestUrl,
                timeout: this.timeout,
                cache: this.theOptions.cacheRequest,
            })
                .then((response) => {
                    this.fetchedData = response;

                    if (this.shouldStopWhen(this.fetchedData)) {
                        /**
                         * This event is fired when polling ends successfully
                         * (both for reaching maximum requests count or for stop flag calculated by `shouldStopWhen` prop).
                         *
                         * @event final-data-loaded
                         * @type {Object} response
                         */
                        this.$emit('final-data-loaded', response);
                        this.resolve();

                        return response;
                    }

                    /**
                     * This event is fired when a request ends successfully.
                     *
                     * @event partial-data-loaded
                     * @type {Object} response
                     */
                    this.$emit('partial-data-loaded', response);
                    return this.continuePollData(url, delay);
                })
                .catch((err) => {
                    const { error, response } = err;

                    if (error && error.event && error.event.type === 'timeout') {
                        /**
                         * This event is fired when a request is aborted by timeout.
                         *
                         * @event timeout
                         * @type {Object}
                         */
                        this.$emit('timeout', { error });
                        return this.continuePollData(url, delay);
                    }

                    /**
                     * This event is fired when a request fails.
                     *
                     * @event error
                     * @type {Object}
                     */
                    this.$emit('error', { response });
                    this.reject();

                    return err;
                });
        },
        /**
         * Start polling.
         *
         * @param {string} url New endpoint url to poll if different from prop `url`
         * @returns {undefined}
         *
         * @public
         */
        start(url = null) {
            if (this.isPending) {
                return;
            }

            this.clear();
            this.requestCount = 0;
            return this.fetchData(url);
        },
        /**
         * Restart polling. It stops every current request and it starts a new one.
         *
         * @param {string} url New endpoint url to poll if different from prop `url`.
         * @returns {undefined}
         *
         * @public
         */
        restart(url = null) {
            if (this.isPending) {
                this.stop();
            }
            return this.start(url);
        },
        /**
         * Stop polling.
         *
         * @returns {undefined}
         *
         * @public
         */
        stop() {
            this.clear();
            this.status = null;
        },
        /**
         * Reject current polling.
         *
         * @returns {undefined}
         *
         * @private
         */
        reject() {
            this.clear();
            this.status = DsPollingPromiseStatusEnum.REJECTED;
        },
        /**
         * Clear next polling queued request.
         *
         * @returns {undefined}
         *
         * @private
         */
        clear() {
            clearTimeout(this.timeoutId);
        },
        /**
         * Resolve current polling.
         *
         * @returns {undefined}
         *
         * @private
         */
        resolve() {
            this.clear();
            this.status = DsPollingPromiseStatusEnum.RESOLVED;
        },
    },
};
</script>
